import React, { useEffect, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { getBoardInfo, getInverterModes, getMeterModels, saveBoardConfig } from '../services/authApi'
import {
  DEFAULT_RS485_COM1_SETTINGS,
  ensureSelectedOption,
  getOptionLabel,
  getOptionValue,
  RS485_BAUD_OPTIONS,
  RS485_DATA_BIT_OPTIONS,
  RS485_FALLBACK_MODEL_OPTIONS,
  RS485_MODE_OPTIONS,
  RS485_PARITY_OPTIONS,
  RS485_STOP_BIT_OPTIONS,
  buildRs485Com1Config,
} from '../services/rs485Settings'
import './DeviceSettingsModal.css'

const HIDDEN_KEY_PATTERN = /(password|secret|token|apikey|api_key|authorization|credential|private)/i

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return {}
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function getDisplayDeviceId(deviceId, boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)
  const device = payload?.config?.Device || payload?.config?.device || payload?.device || {}

  return device.ID || device.Id || device.id || payload?.data?.DeviceID || payload?.data?.deviceId || deviceId
}

function normalizePathKey(value) {
  return String(value).replace(/[\s_-]+/g, '').toLowerCase()
}

function normalizeFormValue(value) {
  if (value === undefined || value === null || value === '') {
    return ''
  }

  return String(value)
}

function toIndexedModelOptions(models) {
  return models.map((model, index) => ({
    label: String(model),
    value: String(index),
  }))
}

function normalizeBoolean(value, fallback = true) {
  if (value === undefined || value === null || value === '') {
    return fallback
  }

  return value === true || value === 1 || String(value).toLowerCase() === 'true' || String(value) === '1'
}

function collectRs485ComEntries(boardInfo, comKey = 'com1') {
  const payload = unwrapBoardInfo(boardInfo)
  const config = payload?.config && typeof payload.config === 'object' ? payload.config : {}
  const entries = []
  const normalizedComKey = normalizePathKey(comKey)

  const visit = (value, path = []) => {
    const normalizedPath = path.map(normalizePathKey)
    const normalizedPathText = normalizedPath.join('')
    const isRs485ComPath = normalizedPathText.includes('rs485') && normalizedPathText.includes(normalizedComKey)
    const currentKey = path[path.length - 1] || ''

    if (HIDDEN_KEY_PATTERN.test(currentKey)) {
      return
    }

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      if (isRs485ComPath && value !== undefined && value !== null && value !== '') {
        entries.push({ key: normalizePathKey(currentKey), path: normalizedPathText, value })
      }
      return
    }

    Object.entries(value).forEach(([key, nestedValue]) => visit(nestedValue, [...path, key]))
  }

  visit(config)

  return entries
}

function getEntryValue(entries, candidateKeys) {
  const candidates = candidateKeys.map(normalizePathKey)
  const entry = entries.find((item) => candidates.some((candidate) => item.key === candidate || item.path.endsWith(candidate)))

  return entry?.value
}

function getRs485ComValues(boardInfo, comKey = 'com1', defaults = DEFAULT_RS485_COM1_SETTINGS) {
  const entries = collectRs485ComEntries(boardInfo, comKey)

  return {
    enabled: normalizeBoolean(getEntryValue(entries, ['enabled', 'enable', 'active', 'status', 'en']), defaults.enabled),
    address: normalizeFormValue(getEntryValue(entries, ['address', 'addr', 'slaveId', 'slaveAddress'])) || defaults.address,
    address2: normalizeFormValue(getEntryValue(entries, ['address2', 'addr2', 'slaveId2', 'slaveAddress2'])) || defaults.address2,
    mode: normalizeFormValue(getEntryValue(entries, ['mode'])) || defaults.mode,
    baudRate: normalizeFormValue(getEntryValue(entries, ['baudRate', 'baud', 'bps'])) || defaults.baudRate,
    dataBit: normalizeFormValue(getEntryValue(entries, ['dataBit', 'dataBits', 'dbit'])) || defaults.dataBit,
    model: normalizeFormValue(getEntryValue(entries, ['model', 'meterModel', 'type'])) || defaults.model,
    model2: normalizeFormValue(getEntryValue(entries, ['model2', 'meterModel2', 'type2'])) || defaults.model2,
    stopBit: normalizeFormValue(getEntryValue(entries, ['stopBit', 'stopBits', 'sbit'])) || defaults.stopBit,
    parity: normalizeFormValue(getEntryValue(entries, ['parity'])) || defaults.parity,
  }
}

function getBoardConfiguration(boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)

  return payload?.config && typeof payload.config === 'object' ? payload.config : {}
}

function buildConfiguration(boardInfo, formValues) {
  const previousConfiguration = getBoardConfiguration(boardInfo)
  const previousRs485 = previousConfiguration.RS485 || {}

  return {
    ...previousConfiguration,
    Date: new Date().toISOString(),
    RS485: {
      ...previousRs485,
      COM1: buildRs485Com1Config(previousRs485.COM1, formValues),
      COM2: previousRs485.COM2 || {
        en: '0',
        addr: '',
        mode: 0,
        model: '',
        baud: '',
        dbit: '',
        sbit: '',
        parity: '',
      },
    },
  }
}

function SelectField({ id, label, options, placeholder, value, onChange }) {
  return (
    <label className="device-settings-modal__field" htmlFor={id}>
      <span>{label}</span>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{placeholder}</option>
        {ensureSelectedOption(options, value).map((option) => (
          <option key={getOptionValue(option)} value={getOptionValue(option)}>
            {getOptionLabel(option)}
          </option>
        ))}
      </select>
    </label>
  )
}

function DeviceSettingsModal({ deviceId, isOpen, onClose }) {
  const [boardInfo, setBoardInfo] = useState(null)
  const [formValues, setFormValues] = useState(DEFAULT_RS485_COM1_SETTINGS)
  const [modelOptions, setModelOptions] = useState(() => toIndexedModelOptions(RS485_FALLBACK_MODEL_OPTIONS))
  const [status, setStatus] = useState('idle')
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const closeButtonRef = useRef(null)
  const displayDeviceId = getDisplayDeviceId(deviceId, boardInfo)
  const isInverter = formValues.mode === '1'

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.()
      }
    }

    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)
    window.setTimeout(() => closeButtonRef.current?.focus(), 0)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen || !deviceId) {
      return undefined
    }

    let isActive = true
    setBoardInfo(null)
    setFormValues(DEFAULT_RS485_COM1_SETTINGS)
    setSaveMessage('')
    setStatus('loading')

    getBoardInfo(deviceId)
      .then((response) => {
        if (!isActive) {
          return
        }

        setBoardInfo(response)
        setFormValues(getRs485ComValues(response, 'com1', DEFAULT_RS485_COM1_SETTINGS))
        setStatus('ready')
      })
      .catch((error) => {
        console.error(`Failed to load settings for ${deviceId}:`, error)

        if (isActive) {
          setBoardInfo(null)
          setStatus('error')
        }
      })

    return () => {
      isActive = false
    }
  }, [deviceId, isOpen])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const isInverter = formValues.mode === '1'
    const loadModels = isInverter ? getInverterModes : getMeterModels
    let isActive = true

    setModelOptions(isInverter ? [] : toIndexedModelOptions(RS485_FALLBACK_MODEL_OPTIONS))

    loadModels()
      .then((nextModels) => {
        if (isActive) {
          setModelOptions(toIndexedModelOptions(nextModels))
        }
      })
      .catch((error) => {
        console.error(`Error fetching ${isInverter ? 'inverter' : 'meter'} models:`, error)
      })

    return () => {
      isActive = false
    }
  }, [formValues.mode, isOpen])

  if (!isOpen || !deviceId) {
    return null
  }

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.()
    }
  }

  const updateFormValue = (key, value) => {
    setSaveMessage('')
    setFormValues((currentValues) => ({
      ...currentValues,
      [key]: value,
      ...(key === 'mode' && value === '1' ? { model: '', address2: '', model2: '' } : {}),
      ...(key === 'mode' && value !== '1' ? { model: '', model2: '' } : {}),
    }))
  }

  const handleSave = async (event) => {
    event.preventDefault()

    if (!displayDeviceId) {
      setSaveMessage('Device ID is required before settings can be saved.')
      return
    }

    const configuration = buildConfiguration(boardInfo, formValues)

    setIsSaving(true)
    setSaveMessage('Saving configuration...')

    try {
      await saveBoardConfig(displayDeviceId, configuration)
      setBoardInfo((currentBoardInfo) => ({
        ...unwrapBoardInfo(currentBoardInfo),
        config: configuration,
      }))
      setSaveMessage('Configuration saved.')
    } catch (error) {
      setSaveMessage(error.message || 'Unable to save the device configuration.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="device-settings-modal" role="presentation" onMouseDown={handleBackdropMouseDown}>
      <section className="device-settings-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="device-settings-modal-title">
        <header className="device-settings-modal__header">
          <div>
            <h2 id="device-settings-modal-title">RS485 Settings</h2>
            <p>{displayDeviceId}</p>
          </div>
          <button className="device-settings-modal__close" type="button" aria-label="Close settings" onClick={onClose} ref={closeButtonRef}>
            <CloseIcon aria-hidden="true" />
          </button>
        </header>

        <div className="device-settings-modal__body">
          {status === 'loading' && <div className="device-settings-modal__status">Loading settings...</div>}
          {status === 'error' && <div className="device-settings-modal__status">Unable to load settings for {deviceId}.</div>}
          {status === 'ready' && (
            <form className="device-settings-modal__form" onSubmit={handleSave}>
              <h3>RS485</h3>

              <div className="device-settings-modal__com" role="group" aria-labelledby="rs485-com1-title">
                <div className="device-settings-modal__com-header">
                  <span className="device-settings-modal__com-title" id="rs485-com1-title">Com 1</span>
                  <label className="device-settings-modal__switch" aria-label="Enable COM1">
                    <input
                      type="checkbox"
                      checked={formValues.enabled}
                      onChange={(event) => updateFormValue('enabled', event.target.checked)}
                    />
                    <span></span>
                  </label>
                </div>

                <div className="device-settings-modal__field-grid">
                  <SelectField
                    id="rs485-com1-baud-rate"
                    label="Baud Rate"
                    options={RS485_BAUD_OPTIONS}
                    placeholder="Select baud rate"
                    value={formValues.baudRate}
                    onChange={(value) => updateFormValue('baudRate', value)}
                  />

                  <SelectField
                    id="rs485-com1-data-bit"
                    label="Data Bit"
                    options={RS485_DATA_BIT_OPTIONS}
                    placeholder="Select data bit"
                    value={formValues.dataBit}
                    onChange={(value) => updateFormValue('dataBit', value)}
                  />

                  <SelectField
                    id="rs485-com1-stop-bit"
                    label="Stop Bit"
                    options={RS485_STOP_BIT_OPTIONS}
                    placeholder="Select stop bit"
                    value={formValues.stopBit}
                    onChange={(value) => updateFormValue('stopBit', value)}
                  />

                  <SelectField
                    id="rs485-com1-parity"
                    label="Parity"
                    options={RS485_PARITY_OPTIONS}
                    placeholder="Select parity"
                    value={formValues.parity}
                    onChange={(value) => updateFormValue('parity', value)}
                  />

                  <SelectField
                    id="rs485-com1-mode"
                    label="Device"
                    options={RS485_MODE_OPTIONS}
                    placeholder="Select device"
                    value={formValues.mode}
                    onChange={(value) => updateFormValue('mode', value)}
                  />

                  <div className={`device-settings-modal__meter-fields ${isInverter ? 'device-settings-modal__meter-fields--single' : ''}`}>
                    <div className="device-settings-modal__meter-column">
                      <h4>{isInverter ? 'Inverter' : 'Meter 1 (Grid)'}</h4>
                      <label className="device-settings-modal__field" htmlFor="rs485-com1-address">
                        <span>Address</span>
                        <input
                          id="rs485-com1-address"
                          type="text"
                          value={formValues.address}
                          onChange={(event) => updateFormValue('address', event.target.value)}
                        />
                      </label>

                      <SelectField
                        id="rs485-com1-model"
                        label="Model"
                        options={modelOptions}
                        placeholder="Select model"
                        value={formValues.model}
                        onChange={(value) => updateFormValue('model', value)}
                      />
                    </div>

                    {!isInverter ? (
                      <div className="device-settings-modal__meter-column">
                        <h4>Meter 2 (Inverter)</h4>
                        <label className="device-settings-modal__field" htmlFor="rs485-com1-address-2">
                          <span>Address</span>
                          <input
                            id="rs485-com1-address-2"
                            type="text"
                            value={formValues.address2}
                            onChange={(event) => updateFormValue('address2', event.target.value)}
                          />
                        </label>

                        <SelectField
                          id="rs485-com1-model-2"
                          label="Model"
                          options={modelOptions}
                          placeholder="Select model"
                          value={formValues.model2}
                          onChange={(value) => updateFormValue('model2', value)}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="device-settings-modal__actions">
                {saveMessage && <p role="status">{saveMessage}</p>}
                <button className="device-settings-modal__cancel" type="button" onClick={onClose} disabled={isSaving}>
                  Cancel
                </button>
                <button className="device-settings-modal__save" type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  )
}

export default DeviceSettingsModal
